import { FC, useEffect, useRef, useState } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import {
  Heading,
  Text,
  Button,
  Image,
  Eyebrow,
  headingType,
  Icon,
  tags,
  size,
} from '@marriott/mi-ui-library';
import { MLogoContainer, MLogoContainerBackgroundColor } from '@marriott/mi-web-ui-kit';

import { StyledComp, LTStyledComp } from './CardHorizontalFeature.styles';
import { CardHorizontalFeatureProps } from './CardHorizontalFeature.types';
import clsx from 'clsx';

/**
 * `CardHorizontalFeature` is a functional React component that renders a customizable card feature.
 *
 * Props:
 * - `eyebrowtext`: Text displayed at the top of the card.
 * - `header`: The main header text of the card.
 * - `description`: The main content or description of the card.
 * - `imageAlignment`: Determines the alignment of the image.
 * - `headerTag`: The HTML tag to be used for the header.
 * - `contentBlockAlignment`: Determines the alignment of the content block.
 * - `linkText`: The text for the link button.
 * - `linkUrl`: The URL that the link button points to.
 * - `ctaType`: The type of call-to-action button.
 * - `openInNewTab`: Boolean indicating whether the link should open in a new tab.
 * - `trackingProperties`: Object containing properties for tracking user interaction.
 * - `styleclass`: Custom class name for styling.
 * - `dynamicMedia`: Object containing information about the dynamic media.
 * - `headerFontSize`: The font size for the header.
 * - `descriptionCustomClass`: Custom class name for the description text.
 * - `headerCustomClass`: Custom class name for the header text.
 * - `custom_click_track_value`: Custom value for tracking click events.
 * - `btnClassName`: Custom class name for button.
 *
 * The component includes an image, a heading, text, and a button, all styled and configured according to the props.
 */

const CardHorizontalFeatureComp: FC<CardHorizontalFeatureProps> = ({
  eyebrowtext,
  header,
  description,
  imageAlignment,
  headerTag = tags.h2,
  contentBlockAlignment,
  linkText,
  linkUrl,
  ctaType,
  openInNewTab = false,
  trackingProperties,
  styleclass,
  dynamicMedia,
  headerFontSize = size.small,
  descriptionCustomClass,
  headerCustomClass,
  custom_click_track_value = '',
  logoIcon,
  logoIconAltText,
  linkText2,
  ctaType2,
  linkUrl2,
  openInNewTab2,
  assetVariation,
  btnClassName,
  creditLine,
  featureFlagLogo = false,
  ...props
}) => {
  const [viewPort, setViewPort] = useState('Desktop');
  const isMobileView = viewPort === 'Mobile';

  const handleResize = () => {
    const xs = window.matchMedia('(max-width: 767px)');
    const md = window.matchMedia('(min-width: 768px) and (max-width: 1199px)');
    const lg = window.matchMedia('(min-width: 1200px)');
    if (xs?.matches) {
      setViewPort('Mobile');
    } else if (md?.matches) {
      setViewPort('Tablet');
    } else if (lg?.matches) {
      setViewPort('Desktop');
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const renditions = dynamicMedia?.renditions;

  const primaryCTAisLink = linkText ? true : false;
  const secondaryCTAisLink = linkText2 ? true : false;

  function getCtaClasses(ctaType: string, openInNewTab: boolean) {
    let classNames = 'button-small ';

    switch (ctaType) {
      case 'primaryButton':
        classNames += 'm-button-primary d-flex align-items-center';
        break;
      case 'secondaryButton':
        classNames += 'm-button-secondary d-flex align-items-center';
        break;
      case 'standaloneLink':
        classNames += 'm-link-action';
        break;
      case 'tertiaryLink':
        classNames += 'm-link-tertiary-button';
        break;
      default:
        break;
    }

    if (openInNewTab) {
      if (ctaType === 'primaryButton' || ctaType === 'secondaryButton') {
        classNames += ' m-button-external';
      } else {
        classNames += ' m-link-tertiary-button-external';
      }
    }

    return classNames.trim();
  }

  const textAlignClasses = contentBlockAlignment === 'center' ? 'text-center' : 'text-left';
  const customAttributes = trackingProperties?.enableScrollingBehavior
    ? { 'data-section-tracking': props?.componentId }
    : {};

  const primaryBtnRef = useRef(null) as unknown as React.MutableRefObject<HTMLInputElement>;

  const secondaryBtnRef = useRef(null) as unknown as React.MutableRefObject<HTMLInputElement>;

  return (
    <StyledComp
      data-component-name="m-ui-library-CardHorizontalFeature"
      data-testid="ui-library-CardHorizontalFeature"
      newTab={openInNewTab}
      alignElement={contentBlockAlignment}
      imgAlignment={imageAlignment}
      isMobileView={isMobileView}
      logoIcon={logoIcon}
      className={clsx(
        `${styleclass ? styleclass : ''}`,
        viewPort === 'Desktop' && assetVariation === 'video' && imageAlignment === 'right' ? 'flex-row-reverse' : '',
        'row justify-content-center'
      )}
      {...customAttributes}
    >
      <div className={clsx('col-12 col-lg-7 p-0 img-container', { 'gradient-color': logoIcon })}>
        <Image
          customClass="feature-img image-cta-row"
          altText={dynamicMedia?.altText}
          renditions={renditions}
          dynamic={dynamicMedia?.dynamic}
          defaultImageURL={dynamicMedia?.assetPath}
        />
        {creditLine && (
          <div className={clsx(`credit-line-wrapper ${imageAlignment || 'left'}`)}>
            <h5 className="credit-line">{creditLine}</h5>
          </div>
        )}
        {logoIcon &&
          (!featureFlagLogo ? (
            <Icon iconClass={clsx(`brand-logo-${logoIcon}`, 'icon-inverse')} ariaLabel={logoIconAltText} />
          ) : (
            <MLogoContainer
              brand={`brand-logo-${logoIcon}`}
              aspectRatio={'3:2'}
              width={74}
              height={50}
              backgroundColor={MLogoContainerBackgroundColor.Inverse}
              alignment={'Bottom Left'}
              color={'background-aware'}
              brandFullName={logoIconAltText}
            />
          ))}
      </div>

      <div
        className={clsx(
          `content ${textAlignClasses} col-12 col-lg-5 t-background-color`,
          props?.colorClass && props?.colorClass
        )}
      >
        {eyebrowtext && <Eyebrow text={eyebrowtext} />}
        <Heading
          customClass={clsx('feature-header', headerCustomClass)}
          dangerouslySet={header}
          variation={headingType.title}
          fontSize={headerFontSize}
          element={headerTag}
        />
        {description && (
          <Text
            customClass={clsx('feature-text t-font-alt-s', descriptionCustomClass)}
            copyText={description}
            fontSize={size.small}
            element={tags.div}
          />
        )}
        <div className="btn-section">
          {linkUrl && linkText && (
            <Button
              isLink={primaryCTAisLink}
              href={linkUrl}
              className={clsx(getCtaClasses(ctaType as string, openInNewTab), btnClassName, 'custom_click_track')}
              target={openInNewTab === true ? '_blank' : '_self'}
              linkType={openInNewTab === true ? 'external' : 'internal'}
              trackingProperties={trackingProperties}
              ariaLabel={linkText}
              custom_click_track_value={
                custom_click_track_value ||
                (trackingProperties?.clickTrack
                  ? `${trackingProperties?.trackingContentPosition || ''}|${
                      trackingProperties?.trackingDescription || ''
                    }|${openInNewTab ? 'external' : 'internal'}`
                  : undefined)
              }
              ref={primaryBtnRef}
            >
              {linkText}
            </Button>
          )}
          {linkUrl2 && linkText2 && (
            <Button
              isLink={secondaryCTAisLink}
              href={linkUrl2}
              className={clsx(getCtaClasses(ctaType2 as string, openInNewTab2 as boolean), 'custom_click_track')}
              target={openInNewTab2 === true ? '_blank' : '_self'}
              linkType={openInNewTab2 === true ? 'external' : 'internal'}
              trackingProperties={trackingProperties}
              ariaLabel={linkText2}
              custom_click_track_value={
                custom_click_track_value ||
                (trackingProperties?.clickTrack
                  ? `${trackingProperties?.trackingContentPosition || ''}|${
                      trackingProperties?.trackingDescription || ''
                    }|${openInNewTab2 ? 'external' : 'internal'}`
                  : undefined)
              }
              ref={secondaryBtnRef}
            >
              {linkText2}
            </Button>
          )}
        </div>
      </div>
    </StyledComp>
  );
};

export const CardHorizontalFeatureConfig = {
  emptyLabel: 'LoveTravelsCardHorizontalFeature',
  isEmpty: () => true,
};

export const CardHorizontalFeature = (props: CardHorizontalFeatureProps) => (
  <LTStyledComp
    data-testid="card-horizontal-feature"
    data-component-name="o-prearrival-lovetravels-cardhorizontalfeature"
  >
    <div className="container">
      <EditableComponent config={CardHorizontalFeatureConfig} {...props}>
        <CardHorizontalFeatureComp {...props} colorClass="lt-bg-blue" />
      </EditableComponent>
    </div>
  </LTStyledComp>
);
